import ReactDOM from "react-dom";
import { Floater } from "frontend/ui-components/floaters/floater";
import { PortalID } from "frontend/modal/portal-types";
import style from "./instance-menu.module.css";
import { BlankCanvasTemplate, CanvasTemplate } from "shared/datamodel/schemas/canvas-template";
import { Project } from "shared/datamodel/schemas/project";
import { Permission } from "shared/datamodel/schemas/user";
import { InviteIcon } from "frontend/icons/invite";
import { CreateCanvasIcon } from "frontend/icons/create-canvas";
import { MembersIcon } from "frontend/icons/members";
import { RenameIcon } from "frontend/icons/rename";
import { TrashIcon } from "frontend/icons/trash";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import { Team } from "shared/datamodel/schemas/team";
import { LeaveIcon } from "frontend/icons/leave";
import classNames from "classnames";
import useStateValue from "frontend/state/value";

export default function InstanceMenu({
  menuButtonRef,
  menuRef,
  setShowMenu,
  setShowInviteModal,
  setShowMembersModal,
  createNewBoard,
  project,
  team,
  renameClicked,
  setShowLeaveModal,
  setShowDeleteModal,
  source,
}: {
  menuButtonRef: React.RefObject<HTMLDivElement>;
  menuRef: React.RefObject<HTMLDivElement>;
  setShowInviteModal: (show: boolean) => void;
  setShowMenu: (show: boolean) => void;
  setShowMembersModal: (show: boolean) => void;
  createNewBoard: ({
    template,
    inputProjectId,
    inputTeamId,
    source,
  }: {
    template: CanvasTemplate;
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) => void;
  project: Project | null;
  team: Team;
  renameClicked: (e: React.MouseEvent) => void;
  setShowLeaveModal: (show: boolean) => void;
  setShowDeleteModal: (show: boolean) => void;
  source?: string;
}) {
  const instanceString = project ? "Project" : "Workspace";
  const instance = project || team;
  const [{ user }] = useStateValue();

  const adminOptions = () => {
    return (
      <>
        <span
          data-testid="invite-members"
          className={style.item}
          onClick={(e) => {
            e.stopPropagation();
            tracking.trackAnalyticsEvent(`invite_to_${instanceString}_button_clicked`, {
              category: consts.TRACKING_CATEGORY.ADMIN_ACTION,
              invite_type: instanceString,
            });
            setShowInviteModal(true);
            setShowMenu(false);
          }}
        >
          <InviteIcon />
          {"Invite members"}
        </span>
        <span
          data-testid="create canvas"
          className={style.item}
          onClick={(e) => {
            e.stopPropagation();
            createNewBoard({
              template: BlankCanvasTemplate,
              inputProjectId: project?.documentId ?? null,
              inputTeamId: team.documentId,
              source: `${instanceString}-context-menu`,
            });
            setShowMenu(false);
          }}
        >
          <CreateCanvasIcon />
          {"Create canvas"}
        </span>
        {source !== "section-menu" && (
          <span
            data-testid={`${instance.name}-members`}
            className={style.item}
            onClick={(e) => {
              e.stopPropagation();
              setShowMembersModal(true);
              setShowMenu(false);
            }}
          >
            <MembersIcon />
            {`${instanceString} members`}
          </span>
        )}
      </>
    );
  };

  const ownerOptions = () => {
    return (
      <>
        <span
          data-testid="invite-members"
          className={style.item}
          onClick={(e) => {
            e.stopPropagation();
            tracking.trackAnalyticsEvent(`invite_to_${instanceString}_button_clicked`, {
              category: consts.TRACKING_CATEGORY.ADMIN_ACTION,
              invite_type: instanceString,
            });
            setShowInviteModal(true);
            setShowMenu(false);
          }}
        >
          <InviteIcon />
          {"Invite members"}
        </span>
        <span
          data-testid="create canvas"
          className={style.item}
          onClick={(e) => {
            e.stopPropagation();
            createNewBoard({
              template: BlankCanvasTemplate,
              inputProjectId: project?.documentId ?? null,
              inputTeamId: team.documentId,
              source: `${instanceString}-context-menu`,
            });
            setShowMenu(false);
          }}
        >
          <CreateCanvasIcon />
          {"Create canvas"}
        </span>
        {source !== "section-menu" && (
          <span
            data-testid={`${instance.name}-members`}
            className={style.item}
            onClick={(e) => {
              e.stopPropagation();
              setShowMembersModal(true);
              setShowMenu(false);
            }}
          >
            <MembersIcon />
            {`${instanceString} members`}
          </span>
        )}
        <span
          data-testid="Rename"
          className={style.item}
          onClick={(e) => {
            e.stopPropagation();
            renameClicked(e);
            setShowMenu(false);
          }}
        >
          <RenameIcon />
          {`Rename ${instanceString}`}
        </span>
        {!(team && team.accountTeam) && (
          <span
            data-testid="Leave"
            className={style.item}
            onClick={(e) => {
              e.stopPropagation();
              setShowLeaveModal(true);
              setShowMenu(false);
            }}
          >
            <LeaveIcon />
            {`Leave ${instanceString}`}
          </span>
        )}
        <span
          data-testid="Delete"
          className={classNames(style.item, style.delete)}
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteModal(true);
            setShowMenu(false);
          }}
        >
          <TrashIcon />
          {`Delete ${instanceString}`}
        </span>
      </>
    );
  };

  const memberOptions = () => {
    return (
      <>
        {source !== "section-menu" && (
          <span
            data-testid={`${instance.name}-members`}
            className={style.item}
            onClick={(e) => {
              e.stopPropagation();
              setShowMembersModal(true);
              setShowMenu(false);
            }}
          >
            <MembersIcon />
            {`${instanceString} members`}
          </span>
        )}
        {instance.permission === Permission.editor && (
          <span
            data-testid="create canvas"
            className={style.item}
            onClick={(e) => {
              e.stopPropagation();
              createNewBoard({
                template: BlankCanvasTemplate,
                inputProjectId: project?.documentId ?? null,
                inputTeamId: team.documentId,
                source: `${instanceString}-context-menu`,
              });
              setShowMenu(false);
            }}
          >
            <CreateCanvasIcon />
            {"Create canvas"}
          </span>
        )}
        {!(team && team.accountTeam) && (
          <span
            data-testid="Leave"
            className={style.item}
            onClick={(e) => {
              e.stopPropagation();
              setShowLeaveModal(true);
              setShowMenu(false);
            }}
          >
            <LeaveIcon />
            {`Leave ${instanceString}`}
          </span>
        )}
      </>
    );
  };

  const renderMenu = () => {
    const element = (
      <Floater
        relativeTo={menuButtonRef}
        side={"bottom"}
        ref={menuRef}
        extraStyle={{
          zIndex: 1000,
          filter: "drop-shadow(0px 5px 10px rgba(0, 0, 0, 0.20))",
        }}
      >
        <div className={style.menu}>
          {team?.accountTeam && user?.isAdmin ? adminOptions() : instance.isOwner ? ownerOptions() : memberOptions()}
        </div>
      </Floater>
    );
    const isClient = typeof document !== "undefined";
    const portalRoot = isClient ? document.getElementById(PortalID.Floaters) : null;
    if (portalRoot) {
      return ReactDOM.createPortal(element, portalRoot);
    }
    return element;
  };

  return renderMenu();
}
